<template>
  <div class="token-management">
    <h1>Token 管理</h1>
    
    <!-- 添加Token按钮 -->
    <div class="header-actions">
      <button @click="showAddModal" class="add-btn">
        <i class="fas fa-plus"></i> 添加 Token
      </button>
    </div>

    <!-- Token列表 -->
    <div class="token-list">
      <div v-if="isLoading" class="loading">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>
      
      <div v-else-if="error" class="error-message">
        {{ error }}
        <button @click="fetchTokens" class="retry-btn">重试</button>
      </div>
      
      <div v-else-if="tokens.length === 0" class="empty-state">
        <i class="fas fa-key"></i>
        <p>暂无Token</p>
      </div>
      
      <div v-else class="token-cards">
        <div 
          v-for="token in tokens" 
          :key="token.token_type" 
          class="token-card"
        >
          <div class="token-header">
            <h3>{{ token.token_type }}</h3>
            <div class="token-actions">
              <button @click="editToken(token)" class="edit-btn">
                <i class="fas fa-edit">编辑</i>
              </button>
              <button @click="deleteToken(token.token_type)" class="delete-btn">
                <i class="fas fa-trash">删除</i>
              </button>
            </div>
          </div>
          <div class="token-value">
            <span class="value-text">{{ maskToken(token.token_value, token.token_type) }}</span>
            <button @click="toggleTokenVisibility(token.token_type)" class="toggle-btn">
              <i :class="visibleTokens[token.token_type] ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 添加/编辑Token弹窗 -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ isEditing ? '编辑' : '添加' }} Token</h3>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveToken">
            <div class="form-group">
              <label for="tokenType">Token 类型</label>
              <input 
                id="tokenType"
                v-model="formData.token_type" 
                type="text" 
                placeholder="请输入Token类型"
                :disabled="isEditing"
                required
              >
            </div>
            <div class="form-group">
              <label for="tokenValue">Token 值</label>
              <textarea 
                id="tokenValue"
                v-model="formData.token_value" 
                placeholder="请输入Token值"
                rows="4"
                required
              ></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">取消</button>
          <button 
            class="confirm-btn" 
            :disabled="isProcessing" 
            @click="saveToken"
          >
            {{ isProcessing ? '处理中...' : (isEditing ? '更新' : '添加') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '../config/api';

export default {
  name: 'TokenManagement',
  data() {
    return {
      tokens: [],
      isLoading: false,
      error: null,
      showModal: false,
      isEditing: false,
      isProcessing: false,
      visibleTokens: {},
      formData: {
        token_type: '',
        token_value: ''
      }
    }
  },
  created() {
    this.fetchTokens();
  },
  methods: {
    async fetchTokens() {
      try {
        this.isLoading = true;
        this.error = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/tokens`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('权限不足，需要管理员权限');
          }
          throw new Error('获取Token列表失败');
        }

        const data = await response.json();
        // 转换后端返回的对象格式为前端期望的数组格式
        if (data.data && typeof data.data === 'object') {
          this.tokens = Object.entries(data.data).map(([key, value]) => ({
            token_type: key,
            token_value: value
          }));
        } else {
          this.tokens = data.data || [];
        }
      } catch (error) {
        console.error('获取Token列表失败:', error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    showAddModal() {
      this.isEditing = false;
      this.formData = {
        token_type: '',
        token_value: ''
      };
      this.showModal = true;
    },

    editToken(token) {
      this.isEditing = true;
      this.formData = {
        token_type: token.token_type,
        token_value: token.token_value
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.isEditing = false;
      this.formData = {
        token_type: '',
        token_value: ''
      };
    },

    async saveToken() {
      try {
        this.isProcessing = true;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/tokens`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '保存Token失败');
        }

        await this.fetchTokens();
        this.closeModal();
        
        // 显示成功消息
        this.showSuccessMessage(this.isEditing ? 'Token更新成功' : 'Token添加成功');
      } catch (error) {
        console.error('保存Token失败:', error);
        this.error = error.message;
      } finally {
        this.isProcessing = false;
      }
    },

    async deleteToken(tokenType) {
      if (!confirm(`确定要删除Token "${tokenType}" 吗？`)) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/tokens/${encodeURIComponent(tokenType)}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '删除Token失败');
        }

        await this.fetchTokens();
        this.showSuccessMessage('Token删除成功');
      } catch (error) {
        console.error('删除Token失败:', error);
        this.error = error.message;
      }
    },

    toggleTokenVisibility(tokenType) {
      this.visibleTokens = {
        ...this.visibleTokens,
        [tokenType]: !this.visibleTokens[tokenType]
      };
    },

    maskToken(tokenValue, tokenType) {
      if (!tokenValue) return '';
      
      if (this.visibleTokens[tokenType]) {
        return tokenValue;
      }
      
      if (tokenValue.length <= 8) {
        return '*'.repeat(tokenValue.length);
      }
      return tokenValue.substring(0, 4) + '*'.repeat(tokenValue.length - 8) + tokenValue.substring(tokenValue.length - 4);
    },

    showSuccessMessage(message) {
      // 简单的成功提示，可以后续替换为更好的通知组件
      alert(message);
    }
  }
}
</script>

<style scoped>
.token-management {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  color: #2c3e50;
  margin-bottom: 30px;
  font-size: 28px;
}

.header-actions {
  margin-bottom: 30px;
  display: flex;
  justify-content: flex-end;
}

.add-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
}

.loading {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4facfe;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background-color: #fee;
  color: #c33;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 20px;
}

.retry-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.token-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.token-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.token-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.token-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.token-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
}

.token-actions {
  display: flex;
  gap: 8px;
}

.edit-btn, .delete-btn {
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-btn {
  color: #007bff;
}

.edit-btn:hover {
  background-color: #e3f2fd;
}

.delete-btn {
  color: #dc3545;
}

.delete-btn:hover {
  background-color: #ffebee;
}

.token-value {
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
}

.value-text {
  flex: 1;
  word-break: break-all;
  color: #495057;
}

.toggle-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.toggle-btn:hover {
  color: #495057;
  background-color: #e9ecef;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4facfe;
  box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
}

.form-group input:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
}

.cancel-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.cancel-btn:hover {
  background: #5a6268;
}

.confirm-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>